import type { RecaptionPrompt, ReferencePrompt, TextPromptWithInputItem } from '@/app/types';

type AudioReference = Extract<ReferencePrompt, { type: 'audio' }>;
type ReferenceForm = {
  referencePrompts?: ReferencePrompt[];
  textPromptWithReference?: TextPromptWithInputItem;
  recaptionPrompt?: RecaptionPrompt;
};

export const findVoiceReference = (references: ReferencePrompt[], voiceId?: string, excludeId?: string) =>
  voiceId?.trim()
    ? references.find(
        (reference): reference is AudioReference =>
          reference.type === 'audio' &&
          reference.id !== excludeId &&
          reference.prompt?.originElementVoiceId?.trim() === voiceId.trim(),
      )
    : undefined;

// References are identified by ID; names are the unique labels sent in [@name].
export const normalizeReferenceForm = <T extends object>(form: T): T => {
  const { referencePrompts, textPromptWithReference, recaptionPrompt } = form as ReferenceForm;
  if (!referencePrompts) return form;
  const aliases = new Map<string, string>();
  const voices = new Map<string, ReferencePrompt>();
  const references = referencePrompts.filter((reference) => {
    const voiceId = reference.type === 'audio' && reference.prompt?.originElementVoiceId?.trim();
    if (!voiceId) return true;
    const previous = voices.get(voiceId);
    if (previous) {
      aliases.set(reference.id, previous.id);
      return false;
    }
    voices.set(voiceId, reference);
    return true;
  });
  const reservedNames = new Set(references.map((reference) => reference.name.trim()));
  const usedNames = new Set<string>();
  const normalized = references.map((reference) => {
    const base = reference.name.trim() || reference.type;
    let name = base;
    let index = 1;
    while (usedNames.has(name)) {
      do {
        name = `${base}${index++}`;
      } while (reservedNames.has(name));
    }
    usedNames.add(name);
    return name === reference.name ? reference : { ...reference, name };
  });
  const byId = new Map(normalized.map((reference) => [reference.id, reference]));
  const updateMentions = (items: TextPromptWithInputItem): TextPromptWithInputItem =>
    items.map((item) => {
      if (item.type !== 'mention') return item;
      const reference = byId.get(aliases.get(item.mention.id) ?? item.mention.id);
      return reference ? { ...item, mention: { ...item.mention, id: reference.id, name: reference.name } } : item;
    });
  return {
    ...form,
    referencePrompts: normalized,
    ...(textPromptWithReference ? { textPromptWithReference: updateMentions(textPromptWithReference) } : {}),
    ...(recaptionPrompt
      ? {
          recaptionPrompt: {
            ...recaptionPrompt,
            subject: updateMentions(recaptionPrompt.subject),
            environment: updateMentions(recaptionPrompt.environment),
          },
        }
      : {}),
  };
};
